import { useCallback, useState } from 'react'
import { Grid2X2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  CATALOG_BROWSE_SEARCH_PARAM,
  parseCategoriesFromUrl,
} from './utils'
import {
  useProductsPageData,
  useProductsSearchData,
  useBreadcrumbCategories,
} from './hooks'
import { ProductGridSection } from './components/ProductGridSection'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Button } from 'src/shared/components/ui/button'
import { Card } from 'src/shared/components/ui/card'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { EmptyState } from 'src/shared/components/EmptyState'
import { CategoryViewer } from 'src/shared/components/navigation/NavBar/TopNavBar/components/AllProductsNavItem/CategoryViewer'
import {
  useEmbeddedCatalogParamsOptional,
  useProductsCatalogSearchParams,
} from './embeddedCatalogParams'
import { ProductsSearchAutocomplete } from './components/ProductsSearchAutocomplete'
import { CatalogShell } from './components/CatalogShell'
import { CatalogHeader } from './components/CatalogHeader'
import { CatalogBreadcrumb } from './components/CatalogBreadcrumb'

export const ProductsPage = ({
  addToCurrentQuoteId,
  showFooter = true,
  embedUseProductPreview = false,
  asSection = false,
  showAddToQuote = true,
}: {
  addToCurrentQuoteId?: number
  showFooter?: boolean
  embedUseProductPreview?: boolean
  asSection?: boolean
  showAddToQuote?: boolean
} = {}) => {
  const [searchParams, setSearchParams] = useProductsCatalogSearchParams()
  const embedded = !!useEmbeddedCatalogParamsOptional()
  const hideCatalogHeader = embedded || asSection
  const { t } = useLocalization()
  const browseCategoryId =
    searchParams.get(CATALOG_BROWSE_SEARCH_PARAM)?.trim() || null

  const setBrowseCategoryId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id) {
            next.set(CATALOG_BROWSE_SEARCH_PARAM, id)
            next.delete('search')
          } else {
            next.delete(CATALOG_BROWSE_SEARCH_PARAM)
          }
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const [productSearchDraft, setProductSearchDraft] = useState('')

  const goToAllCategories = useCallback(() => {
    setProductSearchDraft('')
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('categories')
        next.delete(CATALOG_BROWSE_SEARCH_PARAM)
        next.delete('search')
        return next
      },
      { replace: false }
    )
  }, [setSearchParams])

  const submitProductSearch = useCallback(() => {
    const term = productSearchDraft.trim()
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (term) {
          next.set('search', term)
          next.delete(CATALOG_BROWSE_SEARCH_PARAM)
          next.delete('categories')
        } else {
          next.delete('search')
        }
        return next
      },
      { replace: false }
    )
  }, [productSearchDraft, setSearchParams])

  const searchTerm = searchParams.get('search')?.trim() || null
  const categoryIdsFromUrl = parseCategoriesFromUrl(
    searchParams.get('categories') || ''
  )
  const selectedCategoryId = categoryIdsFromUrl[categoryIdsFromUrl.length - 1]
  const breadcrumbCategories = useBreadcrumbCategories(categoryIdsFromUrl)

  const goToBreadcrumbIndex = useCallback(
    (endIndex: number) => {
      const prefix = categoryIdsFromUrl.slice(0, endIndex + 1)
      if (prefix.length === 0) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('categories', prefix.join(','))
          next.delete(CATALOG_BROWSE_SEARCH_PARAM)
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams, categoryIdsFromUrl]
  )

  const searchData = useProductsSearchData(searchTerm)
  const categoryData = useProductsPageData(selectedCategoryId)

  const isSearchMode = !!searchTerm
  const {
    product_items,
    activeResult,
    page,
    pageSize,
    currentTotalPages,
    onPageChange,
    onPageSizeChange,
    isLoading,
  } = isSearchMode ? searchData : categoryData

  if (isLoading) {
    return <FullScreenLoader />
  }

  const catalogTitle = t('Catalog', 'Katalog')
  const headerIcon = (
    <Grid2X2 size={20} strokeWidth={2} className="shrink-0" />
  )
  const emptyStateIcon = (
    <Grid2X2 size={36} strokeWidth={2} className="shrink-0" />
  )

  if (isSearchMode) {
    return (
      <CatalogShell
        embedded={embedded}
        asSection={asSection}
        showFooter={showFooter}
        width="lg"
      >
        {!hideCatalogHeader && (
          <CatalogHeader icon={headerIcon} title={catalogTitle} />
        )}

        {product_items.length === 0 ? (
          <EmptyState
            icon={emptyStateIcon}
            title={t(
              'No products found for your search',
              'Inga produkter hittades för din sökning'
            )}
            description={t(
              'Try a different search term or browse categories.',
              'Prova ett annat sökord eller bläddra bland kategorier.'
            )}
            button={
              <Button variant="secondary" size="sm" onClick={goToAllCategories}>
                {t('See all categories', 'Visa alla kategorier')}
              </Button>
            }
          />
        ) : (
          <Column className="gap-4">
            <Card className="p-4">
              <Row className="items-center justify-between gap-3 flex-wrap">
                <Row className="items-center gap-2 min-w-0">
                  <h2 className="truncate">
                    {t('Search', 'Sök')}: "{searchTerm}"
                  </h2>
                  <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                    ({activeResult?.count ?? 0})
                  </span>
                </Row>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={goToAllCategories}
                  className="shrink-0"
                >
                  {t('See all categories', 'Visa alla kategorier')}
                </Button>
              </Row>
            </Card>

            <ProductGridSection
              product_items={product_items}
              activeResult={activeResult}
              page={page}
              pageSize={pageSize}
              currentTotalPages={currentTotalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              addToCurrentQuoteId={addToCurrentQuoteId}
              embedUseProductPreview={embedUseProductPreview}
              showAddToQuote={showAddToQuote}
            />
          </Column>
        )}
      </CatalogShell>
    )
  }

  if (categoryIdsFromUrl.length === 0) {
    return (
      <CatalogShell
        embedded={embedded}
        asSection={asSection}
        showFooter={showFooter}
        width="md"
      >
        {!hideCatalogHeader && (
          <CatalogHeader icon={headerIcon} title={catalogTitle} />
        )}

        <Card className="p-4">
          <Column className="gap-5">
            {!browseCategoryId && (
              <ProductsSearchAutocomplete
                draft={productSearchDraft}
                onDraftChange={setProductSearchDraft}
                onSubmit={submitProductSearch}
              />
            )}
            <CategoryViewer
              expandedCategoryId={browseCategoryId}
              onExpandedCategoryChange={setBrowseCategoryId}
            />
          </Column>
        </Card>
      </CatalogShell>
    )
  }

  if (product_items.length === 0) {
    return (
      <CatalogShell
        embedded={embedded}
        asSection={asSection}
        showFooter={showFooter}
        width="lg"
      >
        {!hideCatalogHeader && (
          <CatalogHeader icon={headerIcon} title={catalogTitle} />
        )}

        <CatalogBreadcrumb
          categories={breadcrumbCategories}
          count={activeResult?.count ?? 0}
          onBreadcrumbClick={goToBreadcrumbIndex}
          onBackToCategories={goToAllCategories}
        />

        <EmptyState
          icon={emptyStateIcon}
          title={t(
            'No products in this category',
            'Inga produkter i denna kategori'
          )}
          description={t(
            'Try another category or go back to browse all categories.',
            'Prova en annan kategori eller gå tillbaka för att bläddra bland alla kategorier.'
          )}
          button={
            <Button variant="secondary" size="sm" onClick={goToAllCategories}>
              {t('See all categories', 'Visa alla kategorier')}
            </Button>
          }
        />
      </CatalogShell>
    )
  }

  return (
    <CatalogShell
      embedded={embedded}
      asSection={asSection}
      showFooter={showFooter}
      width="lg"
    >
      {!hideCatalogHeader && (
        <CatalogHeader icon={headerIcon} title={catalogTitle} />
      )}

      <Column className="gap-4">
        <CatalogBreadcrumb
          categories={breadcrumbCategories}
          count={activeResult?.count ?? 0}
          onBreadcrumbClick={goToBreadcrumbIndex}
          onBackToCategories={goToAllCategories}
        />

        <ProductGridSection
          product_items={product_items}
          activeResult={activeResult}
          page={page}
          pageSize={pageSize}
          currentTotalPages={currentTotalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          addToCurrentQuoteId={addToCurrentQuoteId}
          embedUseProductPreview={embedUseProductPreview}
          showAddToQuote={showAddToQuote}
        />
      </Column>
    </CatalogShell>
  )
}
